import React from "react";
import { Phone, Mail, MapPin, Instagram, Facebook } from "lucide-react";
import { Link } from "react-router-dom";
import { SERVICES, CITIES } from "../mock";

const WhatsAppIcon = ({ size = 16, className = "" }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.372-.025-.521-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885M20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.359.101 11.945c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.652a11.882 11.882 0 005.71 1.454h.006c6.585 0 11.946-5.36 11.949-11.945a11.821 11.821 0 00-3.481-8.418" />
  </svg>
);

const Footer = () => {
  return (
    <footer className="bg-[#1B2A4A] text-[#c4cfdb] pt-16 pb-8 px-5 md:px-8">
      <div className="max-w-7xl mx-auto grid md:grid-cols-4 gap-10">
        {/* Brand */}
        <div className="md:col-span-1">
          <div className="flex flex-col leading-none">
            <div className="inline-flex bg-white rounded-xl px-3 py-2 w-fit shadow-sm">
              <img src="/brand/logo.png" alt="Healthsqur" className="h-9 w-auto object-contain" />
            </div>
            <span className="text-[8px] tracking-[0.30em] uppercase text-[#8fa1b5] font-semibold mt-2 ml-1">
              you matter to us
            </span>
          </div>
          <p className="text-[14px] mt-4 leading-relaxed text-[#a9b6c4] max-w-xs">
            Trusted, trained home care professionals delivering care that feels
            like family, backed by clinical rigour.
          </p>
          <a
            href="https://wa.me/919611761212?text=Hi%20Healthsqur%2C%20I'd%20like%20to%20book%20a%20caregiver."
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-5 px-5 py-2.5 rounded-full bg-[#1B2A4A] text-white text-[13px] font-semibold border border-white/20 hover:bg-[#142038] transition-colors"
          >
            <WhatsAppIcon size={15} className="text-white" /> Connect on WhatsApp
          </a>

          <div className="mt-6" data-testid="footer-socials">
            <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-[#8fa1b5] mb-3">Follow us</p>
            <div className="flex items-center gap-3">
              <a
                href="https://www.instagram.com/healthsqur?igsh=MTZjYTI1MjFhcXdlNQ=="
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Healthsqur on Instagram"
                data-testid="footer-instagram-link"
                className="flex items-center justify-center w-10 h-10 rounded-full bg-white/10 text-white hover:bg-[#159A90] hover:scale-105 transition-all"
              >
                <Instagram size={18} />
              </a>
              <a
                href="https://www.facebook.com/share/1AxWLuGTnj/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Healthsqur on Facebook"
                data-testid="footer-facebook-link"
                className="flex items-center justify-center w-10 h-10 rounded-full bg-white/10 text-white hover:bg-[#159A90] hover:scale-105 transition-all"
              >
                <Facebook size={18} />
              </a>
            </div>
          </div>
        </div>

        {/* Services */}
        <div>
          <h4 className="font-heading font-bold text-white text-[15px] mb-4">Services</h4>
          <ul className="space-y-2.5">
            {SERVICES.map((s) => (
              <li key={s.slug}>
                <Link to={`/services/${s.slug}`} className="text-[14px] text-[#a9b6c4] hover:text-[#159A90] transition-colors">
                  {s.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Cities */}
        <div>
          <h4 className="font-heading font-bold text-white text-[15px] mb-4">Cities</h4>
          <ul className="space-y-2.5">
            {CITIES.map((c) => (
              <li key={c} className="flex items-center gap-2">
                <MapPin size={13} className="text-[#159A90]" />
                <span className="text-[14px] text-[#a9b6c4]">{c}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact */}
        <div>
          <h4 className="font-heading font-bold text-white text-[15px] mb-4">Get in touch</h4>
          <ul className="space-y-3">
            <li className="flex items-center gap-3">
              <Phone size={16} className="text-[#159A90]" />
              <span className="text-[14px] text-[#a9b6c4]">+91 96117 61212</span>
            </li>
            <li className="flex items-center gap-3">
              <Mail size={16} className="text-[#159A90]" />
              <span className="text-[14px] text-[#a9b6c4]">care@healthsqur.com</span>
            </li>
            <li className="flex items-start gap-3">
              <MapPin size={16} className="text-[#159A90] mt-0.5" />
              <span className="text-[14px] text-[#a9b6c4]">Available across 6 major cities in India</span>
            </li>
            <li className="pt-2 flex flex-col gap-2">
              <Link to="/partner" className="text-[14px] text-[#a9b6c4] hover:text-[#159A90] transition-colors">Partner With Us</Link>
              <Link to="/careers" className="text-[14px] text-[#a9b6c4] hover:text-[#159A90] transition-colors">Careers</Link>
            </li>
          </ul>
        </div>
      </div>

      <div className="max-w-7xl mx-auto mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-3">
        <p className="text-[13px] text-[#8b96a3]">
          &copy; {new Date().getFullYear()} Healthsqur Solutions Pvt Ltd. All rights reserved.
        </p>
        <div className="flex items-center gap-6">
          <Link to="/privacy-policy" data-testid="footer-privacy-link" className="text-[13px] text-[#8b96a3] hover:text-[#159A90] transition-colors">Privacy Policy</Link>
          <Link to="/terms-and-conditions" data-testid="footer-terms-link" className="text-[13px] text-[#8b96a3] hover:text-[#159A90] transition-colors">Terms of Service</Link>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
